import { observer } from "mobx-react";
import { FC, useEffect, useRef, useState } from "react";

import StatusIndicator from "./chat-message/StatusIndicator";
import { useChatStore } from "../../../hooks/useChatStore";
import Column from "../../ui/core/Column";
import GeneratingStatus from "../../ui/GeneratingStatus";

// REV-2301: belt-and-suspenders for any status gap a backend heartbeat fix
// (e.g. REV-2300) misses — if the newest status hasn't changed in this long,
// the turn might just be silently slow, not stuck, so say so.
const STALE_STATUS_MS = 20_000;

const formatElapsed = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

const StatusMessagesRenderer: FC = observer(() => {
  const chatStore = useChatStore();
  const lastStreamTimeRef = useRef<number>(Date.now());

  // Track if we should show the "waiting" fallback status (after 3s of no stream activity)
  const [showWaitingStatus, setShowWaitingStatus] = useState(false);
  // Ticks once a second while a turn is in flight, purely to re-render the
  // "still working" elapsed-time text below — not read directly.
  const [, forceTick] = useState(0);

  // Track stream activity and show "Waiting for agent response..." only after 3s of no activity
  useEffect(() => {
    if (!chatStore.isQuestioning) {
      setShowWaitingStatus(false);
      return;
    }

    // When streaming text changes, we have activity
    if (chatStore.isStreaming) {
      lastStreamTimeRef.current = Date.now();
      setShowWaitingStatus(false);
    }

    // Check every second if 3 seconds have passed without stream activity
    const interval = setInterval(() => {
      const elapsed = Date.now() - lastStreamTimeRef.current;
      if (elapsed >= 3000 && !chatStore.statusMessages?.length) {
        setShowWaitingStatus(true);
      }
      // Statuses carry their own timestamp (set server-side), so re-render on
      // every tick rather than tracking activity — the staleness check below
      // reads chatStore.statusMessages directly off the latest render.
      forceTick((t) => t + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [
    chatStore.isQuestioning,
    chatStore.isStreaming,
    chatStore.statusMessages,
  ]);

  const latestStatus =
    chatStore.statusMessages?.[chatStore.statusMessages.length - 1];
  const staleElapsedMs = latestStatus
    ? Date.now() - latestStatus.timestamp
    : 0;
  const showStillWorking =
    chatStore.isQuestioning &&
    !chatStore.isReadOnly &&
    Boolean(latestStatus) &&
    staleElapsedMs >= STALE_STATUS_MS;

  return (
    <Column
      sx={{
        height: "auto",
        maxWidth: "60rem",
        width: "100%",
        marginRight: "auto",
        marginLeft: "auto",
      }}
    >
      {!chatStore.statusMessages ||
      chatStore.statusMessages?.length === 0 ? null : (
        <StatusIndicator statuses={chatStore.statusMessages} />
      )}

      {/* REV-2301: the status list can go stale mid-turn (a long tool/LLM
          call with no new status frame) and look hung with nothing above to
          say otherwise. Surface elapsed time once the newest status is old. */}
      {showStillWorking ? (
        <GeneratingStatus
          text={`Still working… ${formatElapsed(staleElapsedMs)}`}
        />
      ) : null}

      {/* Show generating status when AI is responding (but not when saving or showing status) */}
      {chatStore.isQuestioning &&
        // !isSavingLetter &&
        (!chatStore.statusMessages || chatStore.statusMessages.length === 0) &&
        // Read-only viewers: show auto-refresh note; Owner: show normal status
        (chatStore.isReadOnly ? (
          <GeneratingStatus text="This chat is being processed by another user. Updates will appear automatically." />
        ) : !chatStore.messages?.some((msg) => msg.sender === "other") ? (
          <GeneratingStatus text="Waiting for an agent..." />
        ) : chatStore.isStreaming ? (
          <GeneratingStatus text="Responding..." />
        ) : showWaitingStatus ? (
          <GeneratingStatus text="Waiting for agent response..." />
        ) : null)}
    </Column>
  );
});

export default StatusMessagesRenderer;
